import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboardingDispatch } from '../context/OnboardingContext';

export const TOTAL_STEPS = 3;

export const stepRoutes: Record<number, string> = {
  1: '/onboarding/license-and-id',
  2: '/onboarding/garage-info',
  3: '/onboarding/terms',
};

// Reverse lookup so the CURRENT step is derived from the URL, not from context
// state. The two used to drift: after a refresh or a back-navigation the context
// step could sit ahead of the route, and goNext() (which read context) then computed
// a next step past TOTAL_STEPS and silently no-op'd — Continue looked dead even though
// the save had succeeded (#430). The route is the single source of truth for "where am I".
const routeToStep: Record<string, number> = Object.fromEntries(
  Object.entries(stepRoutes).map(([step, path]) => [path, Number(step)]),
);

function stepFromPath(pathname: string): number {
  const normalized = pathname.replace(/\/+$/, ''); // tolerate a trailing slash
  return routeToStep[normalized] ?? 1;
}

export function useStepNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useOnboardingDispatch();

  const currentStep = stepFromPath(location.pathname);

  const goToStep = (step: number) => {
    if (step >= 1 && step <= TOTAL_STEPS) {
      dispatch({ type: 'SET_STEP', step });
      navigate(stepRoutes[step]);
    }
  };

  const goNext = () => {
    const nextStep = currentStep + 1;
    if (nextStep <= TOTAL_STEPS) {
      goToStep(nextStep);
    }
  };

  const goBack = () => {
    const prevStep = currentStep - 1;
    if (prevStep >= 1) {
      goToStep(prevStep);
    }
  };

  return { goNext, goBack, goToStep, currentStep };
}
